<script setup>
    import { ref, reactive, computed, watch } from 'vue'
    import NavigationBar from '../components/NavigationBar.vue'
    import HeaderComponent from '../components/HeaderComponent.vue'
    import CallApi  from '../utils/callApi.js'
    import Encoding from "encoding-japanese";

    // 認証ログ関連
    const authLogInfo = reactive({
        // 検索期間(開始日時)
        searchStartDate: "",
        // 検索期間(終了日時)
        searchEndDate: "",
        // ユーザーID
        userId: "",
        //認証結果
        status:'all',
        // リクエストID
        reqId:"",
        // 検索結果ログ
        searchAuthLog:[],
        // 詳細ログ
        detailLog:{},
        // ページネーション
        currentPage: 1,
    })
    
    const pageSize = ref(10);
    // 期間(開始日/終了日)inputの参照
    const fromDateInput = ref(null)
    const toDateInput = ref(null)

    // カレンダーアイコン押下でinputの日付ピッカーを開く
    // テンプレート内ではrefは自動でアンラップされ、DOM要素そのものが渡ってくる
    const openDatePicker = (inputEl) => {
        inputEl?.showPicker?.()
    }

    // 認証ログ検索
    const searchAuthLogs = async() => {
        try{
            // パラメータ設定
            const params =  {
                "startDate": (authLogInfo.searchStartDate || '').replaceAll('-', ''),
                "endDate": (authLogInfo.searchEndDate || '').replaceAll('-', ''),
                "kind":authLogInfo.status
            }
            // ユーザーID、リクエストID、状態設定があれば、パラメータに設定する
            if (authLogInfo.userId) {
                params.user_id = authLogInfo.userId
            }
            if (authLogInfo.reqId) {
                params.req_id = authLogInfo.reqId
            }
            if((authLogInfo.status == 'success') || (authLogInfo.status == 'fail')) {
                params.status = authLogInfo.status
            }
            // APIから認証ログを取得
            const data = await CallApi.getAxiosAuthLogs(params);
            authLogInfo.searchAuthLog = data.log_infos.map(log_info => ({
                request_id:log_info?.request_id || "",
                request_time: log_info?.request_time
                    ? log_info.request_time.substring(0, 14).replace(
                        /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/,
                        '$1/$2/$3 $4:$5:$6'
                    )
                : "", // 時間は表示用(YYYY/MM/DD HH:mm:ss)に修正
                user_id:log_info?.user_id || "-",
                user_name:log_info?.user_name || "-",
                result:log_info?.facedetect_result || "",
                err_msg:log_info?.err_msg || "-",
                response_time:log_info?.response_time || "-",
                score:log_info?.score || "",
            }))
            // リクエスト日時順にソート
            authLogInfo.searchAuthLog = await authLogInfo.searchAuthLog.sort((element, nextElement) => {
                        return (element['request_time'] > nextElement['request_time']) ? -1 : 1;
                    })
            // 1件目に戻す
            authLogInfo.currentPage = 1;

        } catch (error) {
            console.error('通信エラー:', error);
        }
    }

    // CSVファイルのエクスポート
    const exportCSV = () => {
        if (authLogInfo.searchAuthLog.length === 0) {
            alert('検索結果がありません。')
            return
        }
        // CSVヘッダー
        const headers = [
            "認証日時",
            "ユーザーID",
            "ユーザー名",
            "リクエストID",
            "認証結果",
            "失敗理由"
        ];
        // CSVレコード
        const rows = authLogInfo.searchAuthLog.map(log => [
            log.request_time,
            log.user_id,
            log.user_name,
            log.request_id,
            log.result == 1 ? "成功" : "失敗",
            log.err_msg || "-"
        ]);
        
        // ヘッダーとコンテンツを結合
        const csvContent = [
            headers,
            ...rows
        ]
            .map(row =>
                row.map(col => `"${String(col ?? "").replace(/"/g, '""')}"`).join(",")
            )
            .join("\n");

        // 文字列をコード配列に変換
        const unicodeArray = Encoding.stringToCode(csvContent);
        // encoding-japanese で文字コード変換
        const sjisArray = Encoding.convert(unicodeArray, {
            to: "SJIS",
            from: "UNICODE",
            type: "array"
        });
        // Blob生成
        const uint8Array = new Uint8Array(sjisArray);
        const blob = new Blob([uint8Array], {
            type: "text/csv"
        });

        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')

        // ダウンロードURL作成
        a.href = url
        a.download = 'auth_logs.csv'
        a.click()
        URL.revokeObjectURL(url)
    }
    // 認証ログの表示
    const displayedAuthLogs = computed(() => {
        
        const logs = authLogInfo.searchAuthLog || [];
        
        // 全件数がページサイズ以下ならそのまま表示
        if (logs.length <= pageSize.value) {
        return logs;
        }
        
        const start =
            (authLogInfo.currentPage - 1) *
            pageSize.value;
        const end = start + Number(pageSize.value);

        return logs.slice(start, end);

    });

    // 認証ログの件数からページネーションの設定
    const totalPages = computed(() => {
        const total = authLogInfo.searchAuthLog?.length || 0;
        return Math.ceil(
            total / Number(pageSize.value)
        );
    });

    // 検索エリアのクリア
    const clearSearchArea = () => {
        authLogInfo.searchStartDate = "";
        authLogInfo.searchEndDate = "";
        authLogInfo.userId = "";
        authLogInfo.status="all"
        authLogInfo.reqId = "";
    }
    // 詳細ログの表示
    const showLogDetail = (log) => {
        authLogInfo.detailLog = log;
    }
    // 詳細ログクリア
    const closeTable = () => {
        authLogInfo.detailLog = {};
    }
    // ページネーション表示判定
    const showPagination = computed(() => {
        return (
            authLogInfo.searchAuthLog && authLogInfo.searchAuthLog.length >
            Number(pageSize.value)
        );
    });
    
    // currentPage が totalPages を超えたら補正する
    watch(totalPages, (newTotalPages) => {
        if (authLogInfo.currentPage > newTotalPages) {
            authLogInfo.currentPage = Math.max(1, newTotalPages);
        }
    })
</script>
<template>
    <HeaderComponent></HeaderComponent>
    <NavigationBar></NavigationBar>
    <main>
        <div class="mainView">
            <div>
                <div class="titleArea">
                    <p class="title">認証ログ</p>
                    <div class="titleTextArea">
                        <p class="titleText">APIによる認証の履歴を確認できます。検索条件を検索して、ログを絞り込むことができます。</p>
                    </div>
                </div>
                <div class="authLogArea">
                    <div class="logFindArea">
                        <div id="inputTermArea">
                            <p class="textLeft">期間</p>
                            <div id="inputTerm">
                                <input type="date" id="fromDate" ref="fromDateInput" v-model="authLogInfo.searchStartDate">
                                <img src="../assets/calendar.png" class="calendar-icon" @click="openDatePicker(fromDateInput)">
                                <span class="mobileLineBreak"></span>
                                <span class="dateRangeSeparator">～</span>
                                <input type="date" id="toDate" ref="toDateInput" v-model="authLogInfo.searchEndDate">
                                <img src="../assets/calendar.png" class="calendar-icon" @click="openDatePicker(toDateInput)">
                            </div>
                        </div>
                        <div id="inputUserIdArea">
                            <p class="textLeft">ユーザーID</p>
                            <input id="inputUserId" type="text" placeholder="ユーザーIDを入力"  v-model="authLogInfo.userId">
                        </div>
                        <div id="inputStatusArea">
                            <p class="textLeft"> 認証結果</p>
                            <select id="inputStatusButton"  v-model="authLogInfo.status">
                                <option value="all">すべて</option>
                                <option value="success">成功</option>
                                <option value="fail">失敗</option>
                            </select>
                        </div>
                        <div id="inputRequestIdArea">
                            <p class="textLeft">リクエストID</p>
                            <input id="inputRequestId" type="text" placeholder="リクエストIDを入力"  v-model="authLogInfo.reqId">
                        </div>
                        <div id="logFindButtonArea">
                            <button type="button" class="exeButton" id="logFindButton" @click="searchAuthLogs">検索</button>
                            <button type="button" class="clearButton" @click="clearSearchArea">クリア</button>
                        </div>
                    </div>
                    <div class="logListArea">
                        <div class="logListTableArea">
                            <div class="tableHeader logListHeader">
                                <span id="findListTitle">検索結果{{ authLogInfo.searchAuthLog.length }}件</span>
                                <button type="button" class='clearButton' id="exportButton" @click="exportCSV">エクスポート(CSV)</button>
                            </div>
                            <table v-if="authLogInfo.searchAuthLog.length" class="logListTable">
                                <thead>
                                    <tr>
                                        <th>認証日時</th>
                                        <th>ユーザーID</th>
                                        <th>ユーザー名</th>
                                        <th>リクエストID</th>
                                        <th>認証結果</th>
                                        <th>失敗理由</th>
                                        <th>詳細</th>
                                    </tr>
                                </thead>
                                <tbody v-if="authLogInfo.searchAuthLog.length">
                                   <tr v-for="(log, key) in displayedAuthLogs" :key="key">
                                        <td>{{ log.request_time }}</td>
                                        <td>{{ log.user_id }}</td>
                                        <td>{{ log.user_name }}</td>
                                        <td>{{ log.request_id }}</td>
                                        <td>
                                            <button type="button" :class="log.result ? 'successView' : 'failView'">
                                                {{ log.result==1 ? '成功' : '失敗' }}
                                            </button>
                                        </td>
                                        <td>{{ log.err_msg ? log.err_msg : '-' }}</td>
                                        <td>
                                            <button type="button" class="exeButton" id="logDetailButton" @click="showLogDetail(log)">表示</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div v-else class="noDataArea">
                                検索結果がありません
                            </div>
                            <div 
                                v-if="showPagination"
                                class="pagination-area"
                            >
                                <div class="pagination">
                                    <button
                                        class="page-btn"
                                        @click="authLogInfo.currentPage--"
                                        :disabled="authLogInfo.currentPage === 1"
                                    >←</button>
                                    <button
                                        v-for="page in totalPages"
                                        :key="page"
                                        class="page-btn"
                                        :class="{ active: authLogInfo.currentPage === page }"
                                        @click="authLogInfo.currentPage = page"
                                    >{{ page }}</button>
                                    <button
                                        class="page-btn"
                                        @click="authLogInfo.currentPage++"
                                        :disabled="authLogInfo.currentPage === totalPages"
                                    >→</button>
                                </div>
                                <select
                                    v-model="pageSize"
                                    class="page-size"
                                    @change="authLogInfo.currentPage = 1"
                                >
                                    <option value="10">10件 / ページ</option>
                                    <option value="20">20件 / ページ</option>
                                    <option value="50">50件 / ページ</option>
                                </select>       
                            </div>
                        </div>
                        <div class="logDetailArea">
                            <div class="tableHeader">
                                <span id="logDetailTitle">認証ログ詳細</span>
                                <button id="clearTableButton" @click="closeTable">✕</button>
                            </div>
                            <div v-if="!authLogInfo.detailLog.request_id" class="emptyDetailLog">   
                                <img src="../assets/authlog_detaillog.png" id="detaillogImage">
                                <p>ログを選択すると</p>
                                <p>詳細が表示されます</p>
                            </div>
                            
                            <table v-else class="logDetailTable">
                                <thead>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th>認証日時</th>
                                        <td>{{authLogInfo.detailLog.request_time}}</td>
                                    </tr>
                                    <tr>
                                        <th id="logReqId">リクエストID</th>
                                        <td>{{authLogInfo.detailLog.request_id}}</td>
                                    </tr>
                                    <tr>
                                        <th id="logUserId">ユーザーID</th>
                                        <td>{{authLogInfo.detailLog.user_id}}</td>
                                    </tr>
                                    <tr>
                                        <th id="logUserId">ユーザー名</th>
                                        <td>{{authLogInfo.detailLog.user_name}}</td>
                                    </tr>
                                    <tr>
                                        <th>認証結果</th>
                                        <td>
                                            <button :class="authLogInfo.detailLog.result ? 'successView' : 'failView'">
                                                {{authLogInfo.detailLog.result ? '成功' : '失敗' }}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>類似度スコア</th>
                                        <td>
                                            {{ authLogInfo.detailLog.score != "" && authLogInfo.detailLog.score >= 0 ? authLogInfo.detailLog.score + ' %' : '-' }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>レスポンス時間</th>
                                        <td>{{authLogInfo.detailLog.response_time}} ms</td>
                                    </tr>
                                    <tr>
                                        <th>失敗理由</th>
                                        <td>{{authLogInfo.detailLog.err_msg}}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>   
</template>

<style scoped>
    .authLogArea {
        padding: 20px 5% 20px 2%;
        width: 100%;
        box-sizing: border-box;
    }
    .logFindArea {
        width: 100%;
        box-sizing: border-box;
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;
        gap: 40px;
        margin: 10px 0px 30px -10px;
        border: 1px solid #969292;
        border-radius: 10px;
        padding: 20px;
    }
    .logListArea{
        display: flex;
        justify-content: center;
        gap: 10px;
        width:100%;
        align-items: stretch;
    }
    .logListTableArea{
        flex: 1;
        min-width: 0;
        border: 1px solid #969292;
        border-radius: 10px;
        overflow-x: auto;
        overflow-y: hidden;
    }
    .logDetailArea{
        border: 1px solid #969292;
        border-radius: 10px;
        flex: 0 0 320px;
        background-color: #fff;
        height: 100%;
    }
    #inputTermArea, #inputUserIdArea, #inputStatusArea, #inputRequestIdArea{
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
    }
    #inputTermArea{
        flex: 0 1 420px;
        min-width: 420px;
    }
    #inputUserIdArea, #inputStatusArea, #inputRequestIdArea{
        flex: 1;
        min-width: 220px;
    }

    #inputUserId, #inputRequestId, #inputStatusButton {
        width:100%;
        height: 50px;
        box-sizing:border-box;
        border: 1px solid #969292;
        border-radius: 5px;
    }
    #inputTerm{
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
        box-sizing: border-box;
        border: 1px solid #969292;
        border-radius: 5px;
        padding: 5px 10px;
        margin-left:10px;
        background-color: white;
    }
    #fromDate, #toDate{
        flex: 0 0 auto;
        width: 160px;
        min-width: 0;
        margin-left: 0;
        border:none;
        outline: none;
        background: transparent;
        font-size: 15px;
        position: relative;
    }
    .calendar-icon{
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        cursor: pointer;
    }
    .dateRangeSeparator{
        display: inline-flex;
        justify-content: center;
        width: 16px;
        flex-shrink: 0;
        margin: 0;
    }
    /* スマホ幅では開始日/終了日を2段に分ける改行用ダミー要素(それ以外の幅では非表示) */
    .mobileLineBreak{
        display: none;
    }
    /* ブラウザ標準のカレンダーアイコンは見た目だけ隠し、独自のアイコンのみをクリック対象にする */
    /* display:none にすると内部レイアウトが再計算され、日付表示が崩れる(文字が欠ける)ことがあるため opacity で隠す */
    #fromDate::-webkit-calendar-picker-indicator,
    #toDate::-webkit-calendar-picker-indicator {
        opacity: 0;
    }
    #inputStatusButton{
        width:100%;
        padding: 5px 10px;
        border: 1px solid #969292;
        border-radius: 5px;
        height: 50px;
    }
    #logFindButtonArea{
        display: flex;
        align-items: right;
        gap: 30px;
        flex:1.2;
        margin-left: auto;
    }
    .tableHeader{
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: #d3d2d3;
        padding: 10px 14px;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        border-bottom: 1px solid black;
    }
    #findListTitle{
        font-weight: bold;
    }
    #clearTableButton{
        border:none;
        background:none;
        cursor: pointer;
    }
    #logDetailTitle{
        font-weight: bold;
        font-size: 16px;
    }
    .logDetailArea .tableHeader {
        padding: 8px 12px;
    }
    #logDetailButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        min-width: 0;
        padding: 4px 10px;
        font-size: 13px;
    }
    .logListTable .successView,
    .logListTable .failView {
        padding: 4px 8px;
        font-size: 13px;
    }
    #detaillogImage{
        width:56px;
        height: 56px;
        margin:12px;
    }
    .emptyDetailLog{
        min-height: 320px;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 13px;
    }
    .logListTable,.logDetailTable {
        margin: 0 0 0 0;
        border-collapse: collapse;
        border: none;
    }
    .logDetailTable {
        width: 100%;
    }
    .logListTable{
        width: 100%;
        table-layout: fixed;
    }
    .logDetailTable {
        min-height:400px;
        table-layout: fixed;
    }
    .logListHeader {
        width: 100%;
        box-sizing: border-box;
    }
    .logListTable th {
        color: #585757;
        background-color: #d6d1d1;
        text-align: left;
        padding: 8px 10px;
        font-size: 13px;
        overflow-wrap: anywhere;
    }
    .logListTable td {
        border-top: solid 1px;
        background-color: white;
        text-align: left;
        padding: 8px 10px;
        font-size: 13px;
        overflow-wrap: anywhere;
    }
    /* 各列の比率(合計100%) : 失敗理由を圧縮し、ユーザーID・ユーザー名・認証結果に余裕を持たせる */
    .logListTable th:nth-child(1), .logListTable td:nth-child(1) { width: 13%; }
    .logListTable th:nth-child(2), .logListTable td:nth-child(2) { width: 10%; }
    .logListTable th:nth-child(3), .logListTable td:nth-child(3) { width: 11%; }
    .logListTable th:nth-child(4), .logListTable td:nth-child(4) { width: 26%; }
    .logListTable th:nth-child(5), .logListTable td:nth-child(5) { width: 10%; }
    .logListTable th:nth-child(6), .logListTable td:nth-child(6) { width: 21%; }
    .logListTable th:nth-child(7), .logListTable td:nth-child(7) { width: 9%; }
    #logReqId, logUserId{
        width:100px;
    }
    .logDetailTable th {
        white-space: nowrap;
        height: 56px;
        color: #585757;
        background-color: white;
        text-align: left;
        width: 90px;
        padding-left: 12px;
        font-size: 13px;
    }
    .logDetailTable td {
        background-color: white;
        text-align: left;
        padding-left: 20px;
        margin:10px;
        width: 70%;
        font-size: 13px;
    }
    .logDetailTable .successView,
    .logDetailTable .failView {
        padding: 4px 8px;
        font-size: 13px;
    }
    .logDetailTable tr:last-child th {
        border-bottom-left-radius: 10px;
    }
    .logDetailTable tr:last-child td {
        border-bottom-right-radius: 10px;
    }
    .noDataArea {
        height: 300px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #666;
    }
    @media (max-width: 1600px){
        .logListArea{
            flex-direction: column;
            display: flex;
            width: 100%;
        }
        .logDetailArea{
            width:100%;
            min-width: auto;
            flex: none;
        }
    }
    @media (max-width: 1280px) {
        .mainView {
            margin-left: 0;
            margin-right: 0;
            padding: 10px;
        }
        .logFindArea {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
        }
        #inputTermArea,
        #inputUserIdArea,
        #inputStatusArea,
        #inputRequestIdArea,
        #logFindButtonArea {
            width: 100%;
            flex: 0 0 auto;
        }
        #inputTerm {
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 10px;
        }
        #inputStatusButton {
            width: 100%;
        }
        #exportButton{
            width:30%;
        }
        .pagination-area {
            flex-wrap: wrap;
            row-gap: 10px;
        }
        .pagination {
            flex-wrap: wrap;
            justify-content: center;
        }
    }
    @media (max-width: 768px) {
        .logFindArea {
            margin: 20px 0;
            padding: 10px;
        }
        #inputTermArea,
        #inputUserIdArea,
        #inputStatusArea,
        #inputRequestIdArea {
            width: 100%;
            min-width:0;
            margin: 0;
        }
        #inputTerm {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            column-gap: 2px;
            row-gap: 8px;
        }
        /* 開始日/終了日を2段に分ける */
        .mobileLineBreak{
            display: block;
            flex-basis: 100%;
            width: 0;
            height: 0;
        }
        #inputTerm, #inputUserId, #inputRequestId,#inputStatusButton{
            width: 100%;
            box-sizing: border-box;
        }
        #logFindButtonArea{
            width: 100%;
            margin-left: 0;
            flex-direction: column;
            gap: 10px;
        }
        #logFindButtonArea button{
            width: 100%;
        }
        #inputUserId, #inputRequestId{
            width: 100%;
        }
        .tableHeader {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
            box-sizing: border-box;
        }
        #findListTitle {
            display: block;
        }
        .page-size {
            width: 100%;
        }
        .authLogArea {
            padding: 10px;
        }
        .logDetailArea {
            width: 100%;
            flex: none;
            margin-top: 15px;
        }
        .logDetailTable th,
        .logDetailTable td {
            padding-left: 12px;
            font-size: 13px;
        }
        .logDetailTable td {
            width: auto;
        }
        /* スマホ幅では列を無理に圧縮せず、横スクロールで一覧全体を見られるようにする */
        /* ヘッダーもテーブルと同じ幅にして、右端が揃うようにする */
        .logListTable, .logListHeader {
            min-width: 760px;
        }
    }
</style>